/**
 * Loads a Playbook Live track's rep briefing: who the rep is meeting and why.
 *
 * The backend writes it when the track is saved, as a third-person rewrite of
 * the AI customer's role, organisation and buying objective. The persona's own
 * fields are second-person instructions to the voice model, so they are never
 * shown; nor are the facts the rep must uncover, which never reach the browser.
 *
 * Best effort: on any failure it stays undefined and the page shows no briefing.
 */
import { useEffect, useState } from 'react';

import { apiService } from '../services/apiService';

/** GET /training/tracks/:id/premise. Either field may be empty (an older track). */
export interface RepBriefing {
  customerRole: string;
  meetingPurpose: string;
}

export function useTrackRepBriefing(trackId: string | null): RepBriefing | undefined {
  const [briefing, setBriefing] = useState<RepBriefing | undefined>();

  useEffect(() => {
    setBriefing(undefined);
    if (!trackId) return undefined;
    let isCancelled = false;

    apiService
      .get<RepBriefing>(`/training/tracks/${trackId}/premise`)
      .then((response) => {
        if (!isCancelled && response?.success && response?.data) setBriefing(response.data);
      })
      .catch((loadError) => {
        // Not a session failure: the session runs without a briefing.
        console.warn('useTrackRepBriefing: could not load the rep briefing', loadError);
      });

    return () => {
      isCancelled = true;
    };
  }, [trackId]);

  return briefing;
}
